import React, { useState, useEffect, useCallback } from 'react';
import { getMonedasPaginadas, deleteMoneda } from '../services/monedaService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, Trash2, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Coins } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function MonedasList() {
    const [monedas, setMonedas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { token } = useAuth();

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const cargarMonedas = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getMonedasPaginadas(token, page, pageSize, debouncedSearch);
            setMonedas(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setError(null);
        } catch (error) {
            console.error('Error al cargar monedas:', error);
            setError('No se pudieron cargar las monedas.');
            toast.error('Error al cargar las monedas');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize, debouncedSearch]);

    useEffect(() => {
        cargarMonedas();
    }, [cargarMonedas]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, pageSize]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta moneda?')) {
            try {
                await deleteMoneda(token, id);
                toast.success('Moneda eliminada exitosamente');
                cargarMonedas();
            } catch (error) {
                console.error('Error al eliminar:', error);
                toast.error('No se pudo eliminar la moneda. Verifique si tiene dependencias.');
            }
        }
    };

    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver a Parametrización"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Coins className="w-5 h-5 text-blue-400" />
                        <h1 className="text-lg font-semibold tracking-tight">Mantenimiento: Catálogo de Monedas</h1>
                    </div>
                </div>

                <Link
                    to="/parametrizacion/monedas/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[0.85rem] font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVA MONEDA
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Mostrar:</label>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border border-gray-300 p-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm disabled:opacity-50"
                        disabled={loading}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-500 italic">divisas por página</span>
                </div>

                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar moneda o símbolo..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm bg-white"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                </div>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative min-h-[400px]">
                {error && (
                    <div className="m-3 p-3 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <div className="min-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-xs uppercase tracking-wider font-bold">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 text-center">ID</th>
                                <th className="px-4 py-3 border-r border-gray-200">Descripción / Divisa</th>
                                <th className="px-4 py-3 border-r border-gray-200 w-32 text-center">Cód. ISO</th>
                                <th className="px-4 py-3 border-r border-gray-200 w-32 text-center">Símbolo</th>
                                <th className="px-4 py-3 text-center w-40">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-gray-600">
                            {loading && monedas.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-12 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                                            <span className="text-blue-600 font-medium">Sincronizando con el servidor...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : monedas.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-12 text-center text-gray-500 flex flex-col items-center gap-2">
                                        <Search className="w-8 h-8 text-gray-300" />
                                        <span className="italic">
                                            {searchTerm
                                                ? `No se encontraron resultados para "${searchTerm}"`
                                                : 'No hay monedas registradas en el sistema.'}
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                monedas.map((moneda) => (
                                    <tr key={moneda.id} className="border-b border-gray-200 hover:bg-[#f1f5f9] transition-colors group">
                                        <td className="px-4 py-3 border-r border-gray-200 text-center font-mono text-xs text-gray-500">
                                            {moneda.id}
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200">
                                            <span className="font-bold text-gray-800 uppercase block leading-tight">{moneda.nombre}</span>
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200 text-center">
                                            <span className="px-2 py-1 bg-slate-100 text-slate-700 font-mono text-xs rounded-sm border border-slate-200">
                                                {moneda.codigoIso}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 border-r border-gray-200 text-center">
                                            <span className="font-bold text-blue-700 text-lg">{moneda.simbolo}</span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center items-center gap-3">
                                                <Link
                                                    to={`/parametrizacion/monedas/${moneda.id}/editar`}
                                                    className="text-blue-600 hover:text-blue-800 font-bold text-[0.7rem] tracking-widest uppercase transition-all flex items-center gap-1 group-hover:scale-105"
                                                    title="Editar registro"
                                                >
                                                    EDITAR
                                                </Link>
                                                <span className="text-gray-300">|</span>
                                                <button
                                                    onClick={() => handleEliminar(moneda.id)}
                                                    className="text-red-500 hover:text-red-700 font-bold text-[0.7rem] tracking-widest uppercase transition-all flex items-center gap-1 group-hover:scale-105"
                                                    title="Eliminar registro"
                                                >
                                                    ELIMINAR
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Paginator Footer */}
            <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 border-t border-gray-200 text-sm">
                <div className="text-gray-500 font-medium">
                    Mostrando <span className="text-slate-700">{startItem} a {endItem}</span> de <span className="text-slate-700">{totalElements}</span> divisas operativas
                    {loading && <RefreshCw className="inline-block w-3.5 h-3.5 ml-2 animate-spin text-blue-500" />}
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setPage(0)}
                        disabled={page === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm transition-colors"
                        title="Primera página"
                    >
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm transition-colors"
                        title="Página anterior"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="px-3 py-1 border border-blue-600 bg-blue-600 text-white font-bold rounded-sm text-xs min-w-[32px] text-center">
                        {page + 1}
                    </div>
                    <span className="text-gray-400 text-xs px-2 font-bold uppercase tracking-tighter">de {totalPages || 1}</span>

                    <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages - 1 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm transition-colors"
                        title="Siguiente página"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setPage(totalPages - 1)}
                        disabled={page >= totalPages - 1 || totalPages === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm transition-colors"
                        title="Última página"
                    >
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}